import { getCurrencySymbol } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '@app/finxone-web-frontend/app/lib/services/multipart-holder/multipart-form-holder.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  FormActionState,
  FormActionType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { paymentValidationsForSEPA } from '@finxone-platform/shared/frontend';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import {
  AccountInfoType,
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
  baseCssConfigStyle,
  isMultiCurrencyAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, debounceTime, map, takeUntil } from 'rxjs';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-currency-input-field-widget',
  templateUrl: './currency-input-field-widget.component.html',
  styleUrls: ['./currency-input-field-widget.component.scss'],
})
export class CurrencyInputFieldWidgetComponent extends FormSubmitter implements OnInit, AfterViewInit {
  @ViewChild('inputFieldContent', { static: false })
  public inputFieldContent: ElementRef;

  public inputFieldForm: FormGroup;

  public inputPlaceholder: string;
  public labelStyle: baseCssConfigStyle;
  public nextPageUrl: string;
  public inputFieldKey: string;
  public currencySymbol: string;
  public currencyCode: string;
  public isError = false;
  public selectedAccount: AccountInfoType | undefined;
  public colorStyle: baseCssConfigStyle;
  public inputStyle: baseCssConfigStyle;
  public inputValue: string;
  public inputMode = 'decimal';
  @Select(AccountState.getAccounts)
  private accountStateData!: Observable<AccountStateModel>;

  paymentValidations: {
    maximumTransactionLimit?: number | undefined;
    beneficiaryAddressRequired?: boolean | undefined;
    beneficiaryBICRequired?: boolean | undefined;
  };
  public maxLength = 22;

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    public override store: Store,
    private configService: ConfigService,
    public formSubmissionService: FormSubmissionService,
    private formHolderService: MultiPartFormHolderService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
  ) {
    super(store);
    this.accountStateData
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((accountData: AccountStateModel) => {
          this.selectedAccount = accountData.accounts.find(
            (account) => account.accountId === accountData.currentAccountId,
          );
          if (this.selectedAccount !== undefined && isMultiCurrencyAccountInfoType(this.selectedAccount)) {
            this.currencySymbol = getCurrencySymbol(accountData.currentMultiCurrencyCode ?? '', 'narrow');
            this.currencyCode = accountData.currentMultiCurrencyCode ?? '';
          } else {
            this.currencySymbol = getCurrencySymbol(this.selectedAccount?.currency ?? '', 'narrow');
            this.currencyCode = this.selectedAccount?.currency ?? '';
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.onLoadComponent();
    // maxLength is set to 22 the maximum number of digits before it converts to scientific display e.g. '3.33E+22'
    this.maxLength = this.dataIn.attri.widgetProperties?.['maxLength'] || this.maxLength;
  }

  ngAfterViewInit(): void {
    this.onSetDynamicStyle();
  }

  private onLoadComponent() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.inputMode = this.attri['inputMode'] ?? 'decimal';
      this.inputPlaceholder = this.attri.widgetProperties['inputPlaceholder'];
      const style = this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.MARGIN,
        BaseCssProperties.PADDING,
        BaseCssProperties.BORDER,
        BaseCssProperties.BACKGROUND_COLOR,
        BaseCssProperties.COLOR,
        BaseCssProperties.BOX_SHADOW,
        BaseCssProperties.TEXTALIGN,
      ]);

      this.contentStyle = {
        ...this.contentStyle,
        ...style,
        ...(this.attri.css_config.inputCustomStyling as baseCssConfigStyle),
      };

      this.labelStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.PADDING]);

      this.colorStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);

      this.inputStyle = {
        ...this.colorStyle,
      };

      this.inputFieldKey = this.attri.widgetProperties['inputFieldKey'];

      this.buildForm();
    }
  }

  private buildForm() {
    this.inputFieldForm = new FormGroup({
      [this.inputFieldKey]: new FormControl('', Validators.required),
    });

    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.CURRENCY_INPUT_FIELD_WIDGET,
      route: '/payments/initiate-a-payment-account-selector',
      form: this.inputFieldForm,
    });

    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);

    const currentStage = this.formHolderService.config.getStage(WidgetNames.CURRENCY_INPUT_FIELD_WIDGET);
    this.inputFieldForm = currentStage.getForm();

    this.subscribeToFormAction().subscribe((response) => {
      this.onGetFormActionData(response);
    });
  }

  public onGetFormActionData(response: FormActionType | null) {
    if (response?.formData?.[this.inputFieldKey]) {
      this.patchNameFormValues(response.formData);
    }

    if (response?.nextPageUrl) {
      this.nextPageUrl = response.nextPageUrl;
    }

    if (response?.type === 'submitStep') this.onSubmitCurrencyForm();
  }

  public onSubmitCurrencyForm(): void {
    const formValues: Record<string, string> = this.inputFieldForm.value;
    this.inputValue = formValues[this.inputFieldKey];
    if (this.inputFieldForm.valid) {
      this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then(() => {
        this.isError = false;
      });
    } else {
      this.isError = true;
      this.inputFieldForm.markAllAsTouched();
    }
  }

  public patchNameFormValues(formData: Record<string, string>) {
    this.isError = this.inputFieldForm.invalid;
    this.inputValue = formData?.[this.inputFieldKey];
    this.inputFieldForm.get(this.inputFieldKey)?.setValue(formData?.[this.inputFieldKey]);

    const formIndex = this.formSubmissionService.currentPageFormGroups.findIndex((item) =>
      item.get(this.inputFieldKey),
    );

    this.formSubmissionService.updateCurrentPageFormGroups(formIndex, this.inputFieldForm);
  }

  /**
   * Handles input field changes, performs validation, and triggers appropriate form submission logic.
   */
  public onChange(): void {
    // Parse the input field value as a floating-point number
    const inputValue = parseFloat(this.inputFieldForm.get(this.inputFieldKey)?.value);

    // Reset the input and submit the form if the value is negative
    if (inputValue < 0) {
      this.inputFieldForm.get(this.inputFieldKey)?.setValue('');
      this.submitFormValues();
      return;
    }

    /**
     * Perform SEPA-specific validations if the payment flow is SEPA
     * @default {EURO}
     */
    if (this.selectedAccount?.currency === 'EUR') {
      this.validateSEPATransactionLimits(inputValue);
      return;
    }

    // Handle default form submission for non-SEPA flows
    this.onSubmitCurrencyForm();
  }

  /**
   * Validates the SEPA transaction limits based on the input value and payment rail configuration.
   * If the input value exceeds defined limits, appropriate errors are set on the form field.
   *
   * @param inputValue - The amount to validate against SEPA transaction limits.
   */
  private validateSEPATransactionLimits(inputValue: number): void {
    // Retrieve the SEPA payment rail type from the state
    const paymentRail = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
    )?.formData?.paymentRail as SEPAPaymentRailTypeEnum;

    // Proceed with validation only if a valid payment rail type is configured
    if (paymentRail) {
      this.paymentValidations = paymentValidationsForSEPA[paymentRail];

      // Check for maximum transaction limit violation (if defined)
      if (
        this.paymentValidations?.maximumTransactionLimit &&
        inputValue > this.paymentValidations.maximumTransactionLimit
      ) {
        this.handleTransactionLimitError(Validators.max(this.paymentValidations?.maximumTransactionLimit));
        return;
      }

      // Clear errors and validate the form field when within limits
      this.clearTransactionLimitErrors();
      this.onSubmitCurrencyForm();
    } else {
      // If no payment rail is defined, proceed to submit the currency form
      this.onSubmitCurrencyForm();
    }
  }

  /**
   * Handles form validation errors for transaction limits by setting appropriate error messages.
   *
   * @param errorKey - The key representing the type of error (e.g., 'invalidMinimumAmount').
   */
  private handleTransactionLimitError(validators: ValidatorFn): void {
    this.inputFieldForm.get(this.inputFieldKey)?.setValidators(validators); // Set the error
    this.inputFieldForm.get(this.inputFieldKey)?.updateValueAndValidity(); // Trigger form validation
    this.submitFormValues(); // Submit the form with the updated state
  }

  /**
   * Clears any transaction limit validation errors on the form field.
   */
  private clearTransactionLimitErrors(): void {
    this.inputFieldForm.get(this.inputFieldKey)?.clearAsyncValidators();
    this.inputFieldForm.get(this.inputFieldKey)?.updateValueAndValidity(); // Trigger form validation
  }

  private submitFormValues(): void {
    const formValues: Record<string, string> = this.inputFieldForm.value;
    this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then(() => {
      this.isError = false;
    });
  }

  public onSetDynamicStyle(): void {
    if (this.dataIn?.attri && this.inputFieldContent?.nativeElement) {
      this.renderer.setStyle(
        this.inputFieldContent.nativeElement,
        '--input-field-widget-font-size',
        `${this.dataIn.attri.css_config?.fontSize}px`,
        2,
      );

      this.renderer.setStyle(
        this.inputFieldContent.nativeElement,
        '--input-field-widget-placeholder-color',
        this.dataIn.attri.css_config?.['inputPlaceholderColor'],
        2,
      );
      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  protected readonly String = String;
}
